<script setup lang="ts">
import { reactive, ref } from 'vue'

import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'

import InvoiceCard from '@/components/rc/work-order-dashboard/InvoiceCard.vue'
import InvoiceCardMasterTable from '@/components/rc/work-order-dashboard/InvoiceCardMasterTable.vue'
import InvoiceCardSummary from '@/components/rc/work-order-dashboard/InvoiceCardSummary.vue'

import ContainerCard from '@/components/rc/work-order-dashboard/ContainerCard.vue'

import { useInvoices } from '@/composables/rc/invoices'
import { getInvoiceCSV } from '@/services/rc.service'

import { OrderBy } from '@/types/shared/filters'

import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { InvoiceStatus, type IInvoiceContainer } from '@/types/rc/invoices'
import type { ICustomer } from '@/types/core/Customer'
import type { IInvoiceFilters } from '@/types/rc/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { downloadFile } from '@/util'
import { NotifyFail } from '@/helpers/core/notifications'

// Panels collapse status
const activeItems = ref<number[]>([])

// Clicked container - Will show breakdown
const containerSelected = ref()

// Filter buffers
const selectedWO = ref<{ number?: string }>()
const selectedStatus = ref<InvoiceStatus>()
const selectedCustomer = ref<ICustomer>()

const filters = reactive<IInvoiceFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  customer_id: undefined,
  status: undefined,
  work_order_number: undefined,
})

const invoiceStatuses = Object.values(InvoiceStatus)

const { getInvoicesQuery, deleteInvoiceMutation, refreshInvoiceMutation } = useInvoices()

// BE Data

const { isPending, isError, data, error } = getInvoicesQuery(filters)

// Invoice action buttons handlers

const handleExport = async (invoiceID: number) => {
  try {
    const result = (await getInvoiceCSV(invoiceID)) as Blob
    downloadFile(result, `invoice_${invoiceID}.csv`)
  } catch (err: unknown) {
    const errMsg = err instanceof Error ? err.message : 'Unexpected error'
    NotifyFail(`There was an error downloading the CSV file. ${errMsg}.`)
  }
}

const handleDelete = async (invoiceID: number) => {
  DeleteConfirmationModal(
    'Delete Invoice',
    'Are you sure you want to delete this invoice? This action cannot be undone',
    invoiceID,
    deleteInvoiceMutation.mutateAsync
  )
}

const handleRefresh = async (invoiceID: number) => {
  DeleteConfirmationModal(
    'Refresh Invoice',
    'Are you sure you want to refresh this invoice? This action will generate it again and reset any previous status',
    invoiceID,
    refreshInvoiceMutation.mutateAsync,
    undefined,
    'Refresh'
  )
}
</script>

<template>
  <!-- Filters -->
  <div class="flex gap-2">
    <RcWorkOrderSelect
      v-model="selectedWO"
      class="w-64"
      @change="
        (wo: { number?: string } | undefined) => {
          filters.work_order_number = wo?.number
        }
      "
    />
    <RcCustomerSelect
      v-model="selectedCustomer"
      class="w-64"
      @change="
        (customer) => {
          filters.customer_id = customer?.id
        }
      "
    />
    <el-select
      v-model="selectedStatus"
      class="w-48"
      placeholder="Status"
      clearable
      @change="(st) => (filters.status = st)"
    >
      <el-option v-for="status of invoiceStatuses" :key="status" :label="status" :value="status" />
    </el-select>
  </div>

  <!-- Error state -->
  <RlErrorAlert v-if="isError && !isPending" :error="error" />

  <!-- Loading state -->
  <div v-if="isPending" class="w-full h-32 flex items-center justify-center">
    <Icon icon="eos-icons:three-dots-loading" height="20" />
  </div>

  <!-- Empty state -->
  <RlEmptyDataset
    v-if="!isPending && !data?.data.length"
    title="Oops, nothing in here"
    subtitle="No invoices found matching the selected filters"
  />

  <!-- Container card to visualize a container's breakdown once clicked -->
  <ContainerCard v-if="!isError && containerSelected" :container="containerSelected" @back="containerSelected = null" />

  <!-- List of collapsible InvoiceCards -->
  <el-collapse
    v-if="!isError && !containerSelected && data?.data.length"
    v-model="activeItems"
    class="flex flex-col gap-spacing-md border-none"
  >
    <InvoiceCard
      v-for="invoice in data?.data"
      :key="invoice.id"
      :expanded="activeItems.includes(invoice.id)"
      :invoice="invoice"
      @export="handleExport"
      @delete="handleDelete"
      @refresh="handleRefresh"
    >
      <template #body="{ invoiceDetails }">
        <!-- Containers table -->
        <InvoiceCardMasterTable
          :containers="invoiceDetails?.containers"
          @row-click="(container: IInvoiceContainer) => (containerSelected = container)"
        />

        <el-divider v-if="invoiceDetails?.summary.length" class="invisible" />

        <!-- Summary table (common) -->
        <InvoiceCardSummary v-if="invoiceDetails?.summary.length" :summary="invoiceDetails?.summary" />
      </template>
    </InvoiceCard>
  </el-collapse>
  <el-pagination
    v-if="data && data.total_pages > 0"
    v-model:current-page="filters.page"
    v-model:page-size="filters.per_page"
    class="self-end"
    :page-sizes="DEFAULT_PAGE_SIZES"
    :page-count="data.total_pages"
    :total="data.total_records"
    :disabled="isPending"
    :layout="DEFAULT_PAGINATION_LAYOUT"
    background
  />
</template>
